import React, { useState, useRef, useMemo, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useT, fmt } from '../i18n/I18nContext';
import type { Translations } from '../i18n/translations/ru';
import { trackEvent } from '../analytics';

// ── Tokens (mirror /example & ReportPreviewBlock) ───────────────────────────
const cardBase: React.CSSProperties = {
  background: 'rgba(255,255,255,0.10)',
  backdropFilter: 'blur(24px)',
  WebkitBackdropFilter: 'blur(24px)',
  border: '1px solid rgba(255,255,255,0.20)',
  borderRadius: 22,
  padding: 'clamp(24px, 3vw, 36px)',
  boxShadow: '0 8px 32px rgba(20,10,40,0.18), 0 1px 0 rgba(255,255,255,0.10) inset',
};

const fieldBase: React.CSSProperties = {
  width: '100%',
  background: 'rgba(255,255,255,0.06)',
  border: '1px solid rgba(255,255,255,0.18)',
  borderRadius: 12,
  padding: '12px 14px',
  fontFamily: 'Manrope, sans-serif',
  fontSize: 15,
  color: 'white',
  outline: 'none',
  transition: 'border-color 0.18s ease, background 0.18s ease',
};

// ── Typography scale (single source of truth, hierarchy-aware) ──────────────
const TYPE = {
  pageH1:    { fontSize: 'clamp(26px, 3.4vw, 38px)', fontWeight: 800, lineHeight: 1.16, letterSpacing: '-0.022em' },
  pageSub:   { fontSize: 'clamp(14px, 1.6vw, 16px)', fontWeight: 400, lineHeight: 1.6 },
  stepEyebrow: { fontSize: 11, fontWeight: 700, letterSpacing: '0.10em', textTransform: 'uppercase' as const },
  stepH2:    { fontSize: 'clamp(18px, 2vw, 22px)', fontWeight: 700, lineHeight: 1.3, letterSpacing: '-0.018em' },
  stepSub:   { fontSize: 14, fontWeight: 400, lineHeight: 1.6 },
  label:     { fontSize: 13, fontWeight: 600, letterSpacing: '-0.005em' },
  hint:      { fontSize: 12.5, fontWeight: 400, lineHeight: 1.55 },
  fineprint: { fontSize: 12, fontWeight: 400, lineHeight: 1.5 },
};

// ── Data ────────────────────────────────────────────────────────────────────
export interface PlanFormData {
  skinType: string | null;
  skin: string;
  hasAllergies: boolean | null;
  allergies: string;
  products: string;
  file: File | null;
  email: string;
}

const initialData: PlanFormData = {
  skinType: null, skin: '', hasAllergies: null, allergies: '',
  products: '', file: null, email: '',
};

const STEPS = ['skin', 'allergies', 'products', 'contact'] as const;
type StepId = typeof STEPS[number];

const emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// ── Reusable pieces ─────────────────────────────────────────────────────────
function StepHeader({ stepNum, total, eyebrow, title, subtitle, t }: {
  stepNum: number; total: number; eyebrow?: string; title: string; subtitle?: string; t: Translations;
}) {
  return (
    <div style={{ marginBottom: 24 }}>
      <p style={{
        ...TYPE.stepEyebrow,
        color: 'rgba(255,255,255,0.45)',
        marginBottom: 12,
      }}>
        {fmt(t.form.stepLabel, { n: stepNum, total })}{eyebrow ? ` · ${eyebrow}` : ''}
      </p>
      <h2 style={{ ...TYPE.stepH2, color: 'white', marginBottom: subtitle ? 8 : 0 }}>
        {title}
      </h2>
      {subtitle && (
        <p style={{ ...TYPE.stepSub, color: 'rgba(255,255,255,0.62)' }}>
          {subtitle}
        </p>
      )}
    </div>
  );
}

function ChoiceCard({
  selected, onClick, children, hint,
}: { selected: boolean; onClick: () => void; children: React.ReactNode; hint?: string }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 14,
        width: '100%',
        padding: '15px 18px',
        borderRadius: 14,
        border: selected
          ? '1.5px solid rgba(243,107,175,0.55)'
          : '1px solid rgba(255,255,255,0.18)',
        background: selected
          ? 'rgba(243,107,175,0.12)'
          : 'rgba(255,255,255,0.06)',
        color: 'white',
        fontFamily: 'Manrope,sans-serif',
        fontSize: 15,
        fontWeight: 600,
        textAlign: 'left',
        cursor: 'pointer',
        transition: 'all 0.16s ease',
        letterSpacing: '-0.01em',
      }}
      onMouseEnter={e => {
        if (!selected) {
          (e.currentTarget as HTMLElement).style.background = 'rgba(255,255,255,0.10)';
          (e.currentTarget as HTMLElement).style.borderColor = 'rgba(255,255,255,0.28)';
        }
      }}
      onMouseLeave={e => {
        if (!selected) {
          (e.currentTarget as HTMLElement).style.background = 'rgba(255,255,255,0.06)';
          (e.currentTarget as HTMLElement).style.borderColor = 'rgba(255,255,255,0.18)';
        }
      }}
    >
      <div>
        <div>{children}</div>
        {hint && (
          <div style={{ ...TYPE.hint, fontWeight: 400, color: 'rgba(255,255,255,0.52)', marginTop: 3 }}>
            {hint}
          </div>
        )}
      </div>
      {selected && (
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" style={{ flexShrink: 0 }}>
          <circle cx="10" cy="10" r="9" stroke="rgba(243,107,175,0.7)" strokeWidth="1.4" fill="rgba(243,107,175,0.14)" />
          <path d="M6 10l2.5 2.5L14 7" stroke="#F36BAF" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      )}
    </button>
  );
}

function FileDropzone({ file, onFile, compact, t }: { file: File | null; onFile: (f: File | null) => void; compact?: boolean; t: Translations }) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [drag, setDrag] = useState(false);

  return (
    <div
      onClick={() => inputRef.current?.click()}
      onDragOver={e => { e.preventDefault(); setDrag(true); }}
      onDragLeave={() => setDrag(false)}
      onDrop={e => { e.preventDefault(); setDrag(false); const f = e.dataTransfer.files[0]; if (f) onFile(f); }}
      style={{
        border: `1.5px dashed ${drag ? 'rgba(243,107,175,0.55)' : 'rgba(255,255,255,0.25)'}`,
        background: drag ? 'rgba(243,107,175,0.08)' : 'rgba(255,255,255,0.04)',
        borderRadius: 14,
        padding: compact ? '20px 16px' : '32px 18px',
        textAlign: 'center',
        cursor: 'pointer',
        transition: 'all 0.18s ease',
      }}
    >
      <input ref={inputRef} type="file" accept="image/*" style={{ display: 'none' }}
             onChange={e => onFile(e.target.files?.[0] ?? null)} />
      <svg width={compact ? 22 : 28} height={compact ? 22 : 28} viewBox="0 0 24 24" fill="none"
           style={{ margin: '0 auto', display: 'block', marginBottom: compact ? 8 : 12 }}>
        <path d="M7 16a4 4 0 01-1-7.87A5.5 5.5 0 0117 8.5a4.5 4.5 0 010 9H7z"
              stroke="rgba(255,255,255,0.65)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M12 14V9M9.5 11.5L12 9l2.5 2.5"
              stroke="rgba(255,255,255,0.65)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
      <p style={{ fontSize: 13.5, color: 'rgba(255,255,255,0.82)', fontWeight: 500 }}>
        {file ? file.name : t.form.products.dropzonePrompt}
      </p>
      {file && (
        <button type="button" onClick={e => { e.stopPropagation(); onFile(null); }}
          style={{
            marginTop: 8, background: 'transparent', border: 'none',
            color: 'rgba(255,255,255,0.55)', fontSize: 12, fontFamily: 'Manrope,sans-serif',
            cursor: 'pointer', textDecoration: 'underline',
          }}>
          {t.form.products.removeFile}
        </button>
      )}
    </div>
  );
}

// ── Component ───────────────────────────────────────────────────────────────
type Phase = 'form' | 'loading' | 'limit';

export interface PlanFormProps {
  onSubmit?: (data: PlanFormData) => void;
  price?: { current: string; old?: string };
  skinTypeFromQuery?: string | null;
  /** Skip the page-level H1 + subtitle when embedded inside another section. */
  hideHeading?: boolean;
}

export default function PlanForm({ onSubmit, price = { current: '$4.99', old: '$8.90' }, skinTypeFromQuery, hideHeading }: PlanFormProps) {
  const t = useT();
  const [data, setData] = useState<PlanFormData>(initialData);
  const [step, setStep] = useState(0);
  const [direction, setDirection] = useState<'forward' | 'back'>('forward');
  const [phase, setPhase] = useState<Phase>('form');

  useEffect(() => {
    if (skinTypeFromQuery) {
      setData(d => ({ ...d, skinType: skinTypeFromQuery }));
    } else if (typeof window !== 'undefined') {
      const sp = new URLSearchParams(window.location.search).get('skin');
      if (sp) setData(d => ({ ...d, skinType: sp }));
    }
  }, [skinTypeFromQuery]);

  const stepId = STEPS[step];
  const total  = STEPS.length;

  const canProceed = useMemo(() => {
    switch (stepId) {
      case 'skin':       return data.skin.trim().length >= 5;
      case 'allergies':
        if (data.hasAllergies === null) return false;
        if (data.hasAllergies && data.allergies.trim().length < 2) return false;
        return true;
      case 'products':   return true; // optional — both inputs may be empty
      case 'contact':    return emailRe.test(data.email.trim());
    }
  }, [stepId, data]);

  const MAKE_WEBHOOK_URL = process.env.REACT_APP_MAKE_WEBHOOK_URL ?? '';

  const next = () => {
    if (!canProceed) return;
    if (step === total - 1) {
      trackEvent('plan_submitted', { skin_type: data.skinType ?? 'unknown' });
      onSubmit?.(data);
      setPhase('loading');
      if (MAKE_WEBHOOK_URL) {
        fetch(MAKE_WEBHOOK_URL, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            email: data.email.trim(),
            skinType: data.skinType ?? '',
            skin: data.skin.trim(),
            hasAllergies: data.hasAllergies ?? false,
            allergies: data.allergies.trim(),
            products: data.products.trim(),
          }),
        }).catch(() => {/* silent — user flow continues regardless */});
      }
      window.setTimeout(() => setPhase('limit'), 2400);
      return;
    }
    trackEvent('form_step_completed', { step_number: step + 1, step_name: stepId });
    setDirection('forward');
    setStep(s => s + 1);
  };
  const back = () => {
    if (step === 0) return;
    setDirection('back');
    setStep(s => s - 1);
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey && (e.target as HTMLElement).tagName !== 'TEXTAREA') {
      e.preventDefault();
      next();
    }
  };

  // ── Phase: Loading ────────────────────────────────────────────────────────
  if (phase === 'loading') {
    return (
      <div style={{ ...cardBase, textAlign: 'center', padding: 'clamp(48px, 6vw, 72px) clamp(24px, 3vw, 36px)' }}>
        <div className="pf-spinner" />
        <h3 style={{ ...TYPE.stepH2, color: 'white', marginTop: 28, marginBottom: 8 }}>
          {t.form.loading.title}
        </h3>
        <p style={{ ...TYPE.stepSub, color: 'rgba(255,255,255,0.62)' }}>
          {t.form.loading.subtitle}
        </p>
        <FormStyles />
      </div>
    );
  }

  // ── Phase: Limit reached ──────────────────────────────────────────────────
  if (phase === 'limit') {
    return (
      <div style={{ ...cardBase, textAlign: 'center', padding: 'clamp(40px, 5vw, 56px) clamp(24px, 3vw, 36px)' }}>
        <div style={{
          width: 56, height: 56, borderRadius: '50%',
          margin: '0 auto 22px',
          background: 'rgba(243,107,175,0.16)',
          border: '1px solid rgba(243,107,175,0.32)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <svg width="26" height="26" viewBox="0 0 24 24" fill="none">
            <path d="M7 3h10M7 21h10M8 3v3l4 4-4 4v4M16 3v3l-4 4 4 4v4"
              stroke="#F36BAF" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </div>
        <h3 style={{ fontSize: 'clamp(22px, 2.6vw, 26px)', fontWeight: 800, color: 'white', letterSpacing: '-0.02em', marginBottom: 12, lineHeight: 1.25 }}>
          {t.form.limit.titleA}<br />{t.form.limit.titleB}
        </h3>
        <p style={{ ...TYPE.stepSub, color: 'rgba(255,255,255,0.62)', maxWidth: 380, margin: '0 auto 24px' }}>
          {t.form.limit.body}
        </p>
        <Link
          to="/"
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 7,
            padding: '12px 22px',
            background: 'rgba(255,255,255,0.10)',
            border: '1px solid rgba(255,255,255,0.24)',
            color: 'rgba(255,255,255,0.92)',
            borderRadius: 100,
            fontFamily: 'Manrope,sans-serif',
            fontWeight: 600, fontSize: 14,
            textDecoration: 'none',
            letterSpacing: '-0.01em',
            transition: 'background 0.18s ease, border-color 0.18s ease',
          }}
          onMouseEnter={e => { (e.currentTarget as HTMLElement).style.background = 'rgba(255,255,255,0.18)'; (e.currentTarget as HTMLElement).style.borderColor = 'rgba(255,255,255,0.42)'; }}
          onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = 'rgba(255,255,255,0.10)'; (e.currentTarget as HTMLElement).style.borderColor = 'rgba(255,255,255,0.24)'; }}
        >
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
            <path d="M9 3l-4 4 4 4" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
          {t.form.limit.cta}
        </Link>
        <p style={{ ...TYPE.fineprint, color: 'rgba(255,255,255,0.40)', marginTop: 20, lineHeight: 1.55 }}>
          {t.form.limit.footer}
        </p>
        <FormStyles />
      </div>
    );
  }

  // ── Phase: Form ───────────────────────────────────────────────────────────
  const progress = ((step + 1) / total) * 100;

  return (
    <div onKeyDown={onKeyDown}>
      {/* Page-level heading (outside the card → top of visual hierarchy) */}
      {!hideHeading && (
        <div style={{ textAlign: 'center', marginBottom: 'clamp(28px, 3vw, 40px)' }}>
          <h1 style={{ ...TYPE.pageH1, color: 'white', marginBottom: 12 }}>
            {t.form.pageH1A}<br />{t.form.pageH1B}
          </h1>
          <p style={{ ...TYPE.pageSub, color: 'rgba(255,255,255,0.62)' }}>
            {t.form.pageSub}
          </p>
        </div>
      )}

      {/* Form card */}
      <div style={cardBase}>
        {/* Progress */}
        <div style={{ marginBottom: 26 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
            <span style={{ ...TYPE.stepEyebrow, color: 'rgba(255,255,255,0.55)' }}>
              {t.form.progress}
            </span>
            <span style={{ fontSize: 12, color: 'rgba(255,255,255,0.50)', fontWeight: 600 }}>
              {step + 1} / {total}
            </span>
          </div>
          <div style={{ height: 4, background: 'rgba(255,255,255,0.08)', borderRadius: 4, overflow: 'hidden' }}>
            <div style={{
              width: `${progress}%`, height: '100%',
              background: 'linear-gradient(90deg, #F36BAF, #8B7CFF)',
              borderRadius: 4,
              transition: 'width 0.32s cubic-bezier(.5,.0,.2,1)',
            }} />
          </div>
        </div>

        {/* Step body — re-keyed so the entrance animation runs each transition */}
        <div key={`${step}-${direction}`} className={direction === 'forward' ? 'pf-step-fwd' : 'pf-step-back'}>
          {stepId === 'skin'      && <StepSkin      data={data} setData={setData} total={total} t={t} />}
          {stepId === 'allergies' && <StepAllergies data={data} setData={setData} total={total} t={t} />}
          {stepId === 'products'  && <StepProducts  data={data} setData={setData} total={total} t={t} />}
          {stepId === 'contact'   && <StepContact   data={data} setData={setData} total={total} t={t} />}
        </div>

        {/* Navigation */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 12,
          marginTop: 24,
        }}>
          <button
            type="button"
            onClick={back}
            disabled={step === 0}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              padding: '12px 18px',
              background: 'transparent',
              color: step === 0 ? 'rgba(255,255,255,0.25)' : 'rgba(255,255,255,0.72)',
              border: 'none',
              borderRadius: 100,
              fontFamily: 'Manrope,sans-serif',
              fontWeight: 600, fontSize: 14,
              cursor: step === 0 ? 'default' : 'pointer',
              transition: 'color 0.18s ease',
            }}
            onMouseEnter={e => { if (step !== 0) (e.currentTarget as HTMLElement).style.color = 'white'; }}
            onMouseLeave={e => { if (step !== 0) (e.currentTarget as HTMLElement).style.color = 'rgba(255,255,255,0.72)'; }}
          >
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
              <path d="M9 3l-4 4 4 4" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            {t.form.back}
          </button>

          <button
            type="button"
            onClick={next}
            disabled={!canProceed}
            className={step === total - 1 ? 'pf-cta pf-cta-final' : 'pf-cta'}
            style={{
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8,
              padding: step === total - 1 ? '15px 28px' : '13px 24px',
              background: canProceed
                ? 'linear-gradient(135deg, #F36BAF 0%, #8B7CFF 100%)'
                : 'rgba(255,255,255,0.12)',
              color: 'white',
              border: 'none',
              borderRadius: 100,
              fontFamily: 'Manrope,sans-serif',
              fontWeight: 700,
              fontSize: step === total - 1 ? 15 : 14,
              cursor: canProceed ? 'pointer' : 'not-allowed',
              opacity: canProceed ? 1 : 0.55,
              letterSpacing: '-0.01em',
              transition: 'opacity 0.2s ease',
            }}
          >
            {step === total - 1 ? (
              <>
                {fmt(t.form.submit, { price: price.current })}
                {price.old && (
                  <span style={{ fontSize: 12, opacity: 0.78, textDecoration: 'line-through', marginLeft: 4 }}>
                    {price.old}
                  </span>
                )}
              </>
            ) : (
              <>
                {t.form.next}
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                  <path d="M5 3l4 4-4 4" stroke="white" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </>
            )}
          </button>
        </div>

        {step === total - 1 && (
          <p style={{
            textAlign: 'center',
            ...TYPE.fineprint,
            color: 'rgba(255,255,255,0.40)',
            marginTop: 14,
          }}>
            {t.form.fineprint}
          </p>
        )}
      </div>

      <FormStyles />
    </div>
  );
}

// ── Steps ───────────────────────────────────────────────────────────────────

function StepSkin({ data, setData, total, t }: { data: PlanFormData; setData: React.Dispatch<React.SetStateAction<PlanFormData>>; total: number; t: Translations }) {
  const skinTypeText = data.skinType
    ? (t.form.skinTypes[data.skinType as keyof Translations['form']['skinTypes']] || null)
    : null;
  const startedRef = React.useRef(false);
  return (
    <>
      <StepHeader
        stepNum={1} total={total} t={t}
        eyebrow={skinTypeText ? fmt(t.form.skin.eyebrowSkinType, { type: skinTypeText }) : undefined}
        title={t.form.skin.title}
        subtitle={t.form.skin.subtitle}
      />
      <textarea
        autoFocus
        value={data.skin}
        onChange={e => {
          if (!startedRef.current && e.target.value.length > 0) {
            startedRef.current = true;
            trackEvent('form_started');
          }
          setData(d => ({ ...d, skin: e.target.value }));
        }}
        placeholder={t.form.skin.placeholder}
        rows={5}
        style={{ ...fieldBase, resize: 'vertical', minHeight: 120, fontFamily: 'Manrope,sans-serif' }}
        onFocus={e => { e.target.style.borderColor = 'rgba(255,255,255,0.42)'; }}
        onBlur={e =>  { e.target.style.borderColor = 'rgba(255,255,255,0.18)'; }}
      />
      <p style={{ ...TYPE.hint, color: 'rgba(255,255,255,0.42)', marginTop: 8 }}>
        {t.form.skin.hint}
      </p>
    </>
  );
}

function StepAllergies({ data, setData, total, t }: { data: PlanFormData; setData: React.Dispatch<React.SetStateAction<PlanFormData>>; total: number; t: Translations }) {
  return (
    <>
      <StepHeader
        stepNum={2} total={total} t={t}
        title={t.form.allergies.title}
        subtitle={t.form.allergies.subtitle}
      />

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 16 }}>
        <ChoiceCard
          selected={data.hasAllergies === true}
          onClick={() => { trackEvent('allergy_answer_selected', { answer: 'yes' }); setData(d => ({ ...d, hasAllergies: true })); }}
        >
          {t.form.allergies.yes}
        </ChoiceCard>
        <ChoiceCard
          selected={data.hasAllergies === false}
          onClick={() => { trackEvent('allergy_answer_selected', { answer: 'no' }); setData(d => ({ ...d, hasAllergies: false, allergies: '' })); }}
        >
          {t.form.allergies.no}
        </ChoiceCard>
      </div>

      {data.hasAllergies === true && (
        <div className="pf-conditional">
          <label style={{ display: 'block', ...TYPE.label, color: 'rgba(255,255,255,0.85)', marginBottom: 8 }}>
            {t.form.allergies.labelDetails}
          </label>
          <textarea
            autoFocus
            value={data.allergies}
            onChange={e => setData(d => ({ ...d, allergies: e.target.value }))}
            placeholder={t.form.allergies.placeholder}
            rows={3}
            style={{ ...fieldBase, resize: 'vertical', minHeight: 84, fontFamily: 'Manrope,sans-serif' }}
            onFocus={e => { e.target.style.borderColor = 'rgba(255,255,255,0.42)'; }}
            onBlur={e =>  { e.target.style.borderColor = 'rgba(255,255,255,0.18)'; }}
          />
        </div>
      )}

      {data.hasAllergies === false && (
        <p style={{
          padding: '12px 14px',
          background: 'rgba(157,239,214,0.10)',
          border: '1px solid rgba(157,239,214,0.28)',
          borderRadius: 12,
          ...TYPE.stepSub,
          color: 'rgba(255,255,255,0.78)',
        }}>
          {t.form.allergies.okMessage}
        </p>
      )}
    </>
  );
}

function StepProducts({ data, setData, total, t }: { data: PlanFormData; setData: React.Dispatch<React.SetStateAction<PlanFormData>>; total: number; t: Translations }) {
  return (
    <>
      <StepHeader
        stepNum={3} total={total} t={t}
        title={t.form.products.title}
        subtitle={t.form.products.subtitle}
      />

      <label style={{ display: 'block', ...TYPE.label, color: 'rgba(255,255,255,0.85)', marginBottom: 8 }}>
        {t.form.products.labelText}
      </label>
      <textarea
        value={data.products}
        onChange={e => setData(d => ({ ...d, products: e.target.value }))}
        placeholder={t.form.products.placeholderText}
        rows={4}
        style={{ ...fieldBase, resize: 'vertical', minHeight: 100, fontFamily: 'Manrope,sans-serif' }}
        onFocus={e => { e.target.style.borderColor = 'rgba(255,255,255,0.42)'; }}
        onBlur={e =>  { e.target.style.borderColor = 'rgba(255,255,255,0.18)'; }}
      />

      {/* "и/или" divider */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, margin: '16px 0' }}>
        <div style={{ flex: 1, height: 1, background: 'rgba(255,255,255,0.10)' }} />
        <span style={{ ...TYPE.hint, color: 'rgba(255,255,255,0.42)', fontWeight: 500 }}>{t.form.products.orDivider}</span>
        <div style={{ flex: 1, height: 1, background: 'rgba(255,255,255,0.10)' }} />
      </div>

      <label style={{ display: 'block', ...TYPE.label, color: 'rgba(255,255,255,0.85)', marginBottom: 8 }}>
        {t.form.products.labelPhoto}
      </label>
      <FileDropzone
        compact
        file={data.file}
        onFile={f => { if (f) trackEvent('file_uploaded', { file_type: f.type }); setData(d => ({ ...d, file: f })); }}
        t={t}
      />
      <p style={{ ...TYPE.hint, color: 'rgba(255,255,255,0.42)', marginTop: 8 }}>
        {t.form.products.hint}
      </p>
    </>
  );
}

function StepContact({ data, setData, total, t }: { data: PlanFormData; setData: React.Dispatch<React.SetStateAction<PlanFormData>>; total: number; t: Translations }) {
  return (
    <>
      <StepHeader
        stepNum={4} total={total} t={t}
        title={t.form.contact.title}
        subtitle={t.form.contact.subtitle}
      />

      <input
        autoFocus
        type="email"
        value={data.email}
        onChange={e => setData(d => ({ ...d, email: e.target.value }))}
        placeholder={t.form.contact.placeholder}
        style={fieldBase}
        onFocus={e => { e.target.style.borderColor = 'rgba(255,255,255,0.42)'; }}
        onBlur={e =>  { e.target.style.borderColor = 'rgba(255,255,255,0.18)'; }}
      />
      <p style={{ ...TYPE.hint, color: 'rgba(255,255,255,0.42)', marginTop: 8 }}>
        {t.form.contact.hint}
      </p>
    </>
  );
}

// ── Shared CSS injected once ────────────────────────────────────────────────
function FormStyles() {
  return (
    <style>{`
      @keyframes pfStepFwd  { from { opacity: 0; transform: translateX(18px); }  to { opacity: 1; transform: translateX(0); } }
      @keyframes pfStepBack { from { opacity: 0; transform: translateX(-18px); } to { opacity: 1; transform: translateX(0); } }
      @keyframes pfFadeIn   { from { opacity: 0; transform: translateY(6px); }   to { opacity: 1; transform: translateY(0); } }
      .pf-step-fwd   { animation: pfStepFwd  0.32s cubic-bezier(.5,.0,.2,1) both; }
      .pf-step-back  { animation: pfStepBack 0.32s cubic-bezier(.5,.0,.2,1) both; }
      .pf-conditional { animation: pfFadeIn 0.28s ease both; }

      /* Placeholder colour — single explicit value, no browser tinting */
      input::placeholder, textarea::placeholder {
        color: rgba(255,255,255,0.42);
        opacity: 1;
      }
      input::-webkit-input-placeholder, textarea::-webkit-input-placeholder {
        color: rgba(255,255,255,0.42);
      }

      /* Final-step CTA glow + pulse */
      .pf-cta-final {
        box-shadow:
          0 8px 28px rgba(243,107,175,0.40),
          0 0 36px rgba(243,107,175,0.30),
          0 0 64px rgba(139,124,255,0.18),
          0 0 0 0 rgba(243,107,175,0.40);
        animation: pfPulse 2.6s ease-in-out infinite;
      }
      @keyframes pfPulse {
        0%, 100% {
          box-shadow:
            0 8px 28px rgba(243,107,175,0.40),
            0 0 36px rgba(243,107,175,0.30),
            0 0 64px rgba(139,124,255,0.18),
            0 0 0 0 rgba(243,107,175,0.40);
        }
        50% {
          box-shadow:
            0 12px 42px rgba(243,107,175,0.58),
            0 0 52px rgba(243,107,175,0.48),
            0 0 80px rgba(139,124,255,0.26),
            0 0 0 12px rgba(243,107,175,0);
        }
      }
      .pf-cta-final:disabled { animation: none; box-shadow: none; }

      /* Loading spinner — pink stroke, matches brand */
      .pf-spinner {
        width: 44px; height: 44px;
        margin: 0 auto;
        border-radius: 50%;
        border: 3px solid rgba(243,107,175,0.16);
        border-top-color: #F36BAF;
        border-right-color: rgba(243,107,175,0.55);
        animation: pfSpin 0.95s linear infinite;
      }
      @keyframes pfSpin { to { transform: rotate(360deg); } }

      @media (prefers-reduced-motion: reduce) {
        .pf-step-fwd, .pf-step-back, .pf-cta-final, .pf-spinner, .pf-conditional { animation: none; }
      }
    `}</style>
  );
}
